import { useCallback } from "react";
import { Location, Brand, SelectedFilters } from "../types/mapData";
import {
  filterMarketingGuideLocations,
  sortLocations,
} from "../utils/sortLocations";

interface UseTagsFilterProps {
  initLocations: Location[];
  brands: Record<number, Brand> | null;
  markersState: google.maps.Marker[];
  setCurLoc: (loc: Location | null) => void;
  setCurBrand: (id: number) => void;
  setListState: (state: number) => void;
  setMarkersState: (markers: google.maps.Marker[]) => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setFilteredLocations: (locations: Location[]) => void;
  setInfoOpenMobile: (open: boolean) => void;
  tags: Record<number, any> | null;
  selectedCuisines: SelectedFilters | null;
  selectedMoods: { tags: number[] } | null;
  setTags: (tags: any) => void;
  handleCurMarker: (marker: google.maps.Marker | null) => void;
  handleLocsAndMarkers: (locations: Location[], buildMarkers?: boolean) => void;
  queryParams: { [k: string]: string };
  initSelectedTag: SelectedFilters | null;
  setInitSelectedTag: (tag: SelectedFilters | null) => void;
  map: google.maps.Map | null;
}

export function useTagsFilter({
  initLocations,
  brands,
  markersState,
  setCurLoc,
  setCurBrand,
  setListState,
  setMarkersState,
  setCurrentPage,
  setFilteredLocations,
  setInfoOpenMobile,
  tags,
  selectedCuisines,
  selectedMoods,
  setTags,
  handleCurMarker,
  handleLocsAndMarkers,
  queryParams,
  initSelectedTag,
  setInitSelectedTag,
  map,
}: UseTagsFilterProps) {
  const handleBuildTags = useCallback(
    (tags: any[]) => {
      const tagsObj: Record<number, any> = {};
      for (let i = 0; i < tags.length; i++) {
        tagsObj[tags[i].id] = tags[i];
      }
      setTags(tagsObj);
    },
    [setTags],
  );

  const getFeaturedTags = useCallback(
    (brand?: Brand) => {
      if (!brand?.tags) return [];
      return brand.tags
        .filter((tag) => tag.featured)
        .map((tag) => tags?.[tag.id]?.name)
        .filter((name) => !!name);
    },
    [tags],
  );

  const handleInitSelected = useCallback(() => {
    const startTag = queryParams.tag;
    if (!!startTag && !!tags && !initSelectedTag) {
      const tagsArr = Object.values(tags);
      for (const tag of tagsArr) {
        if (tag.slug === startTag) {
          setInitSelectedTag(tag);
          break;
        }
      }
    }
  }, [queryParams.tag, tags, initSelectedTag, setInitSelectedTag]);

  const hideAllMarkers = useCallback(() => {
    markersState.forEach((marker) => marker.setMap(null));
    setMarkersState([]);
  }, [markersState, setMarkersState]);

  const handleMarketingGuide = (filteredLocations: Location[]) => {
    if (!map) return;
    const bounds = new google.maps.LatLngBounds();
    filteredLocations.forEach((loc) => {
      const lat = loc.location?.latitude;
      const lng = loc.location?.longitude;

      if (lat != null && lng != null) {
        bounds.extend(new google.maps.LatLng(lat, lng));
      }
    });
    map?.fitBounds(bounds, {
      top: 50,
      left: 20,
      right: 20,
    });
  };

  const handleTagsChange = useCallback(() => {
    if (!map) return;
    setListState(1);
    setCurLoc(null);
    handleCurMarker(null);
    setCurBrand(0);
    setInfoOpenMobile(false);

    const cuisineTagIds = selectedCuisines?.tags || [];
    const moodTagIds = selectedMoods?.tags || [];

    if (cuisineTagIds.length === 0 && moodTagIds.length === 0) {
      hideAllMarkers();
      handleLocsAndMarkers(initLocations, true);
      return setFilteredLocations(initLocations);
    }

    const isMarketingGuide =
      initSelectedTag?.category === "Marketing Guide" &&
      selectedCuisines?.tags?.includes(initSelectedTag?.id);
    const newLocs = isMarketingGuide
      ? filterMarketingGuideLocations(initLocations, brands, selectedCuisines)
      : initLocations.filter((loc) => {
          const brand = brands?.[loc.brand_id];
          if (!brand?.tags) return false;
          const brandTagIds = brand.tags.map((tag) => tag.id);
          const matchesCuisine = cuisineTagIds.some((id) =>
            brandTagIds.includes(id),
          );
          const matchesMood = moodTagIds.some((id) => brandTagIds.includes(id));
          return matchesCuisine || matchesMood;
        });
    hideAllMarkers();
    handleLocsAndMarkers(newLocs, true);
    setFilteredLocations(
      sortLocations(
        newLocs,
        brands,
        isMarketingGuide ?? false,
        initSelectedTag?.id ?? 0,
      ),
    );
    setCurrentPage(1);
    if (isMarketingGuide) handleMarketingGuide(newLocs);
  }, [
    selectedCuisines,
    selectedMoods,
    brands,
    initLocations,
    setListState,
    setCurLoc,
    handleCurMarker,
    setCurBrand,
    setInfoOpenMobile,
    hideAllMarkers,
    handleLocsAndMarkers,
    setFilteredLocations,
    setCurrentPage,
  ]);

  return {
    handleTagsChange,
    handleInitSelected,
    handleBuildTags,
    getFeaturedTags,
  };
}
